import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Car, Fuel, Upload, Users, Briefcase, AlertTriangle, Camera, ScanLine } from "lucide-react";
import { useLang } from "@/lib/lang";
import ViewToggle, { useViewMode } from "@/components/admin/ViewToggle";
import OCRUploadModal from "@/components/OCRUploadModal";

const CATEGORIES = [
  { key: "economy", label: "Economy", cap_pax: "1-3", cap_bags: 2,
    desc: "Berlina compacta ideal para até 3 passageiros com bagagem ligeira." },
  { key: "executive", label: "Executive", cap_pax: "1-4", cap_bags: 3,
    desc: "Berlina premium — conforto executivo até 4 passageiros e 3 malas grandes." },
  { key: "xl_group", label: "XL / Grupo", cap_pax: "5-8", cap_bags: 8,
    desc: "Van premium para grupos e famílias — até 8 passageiros e 8 malas." },
];
const FUELS = [
  { k: "diesel", l: "Gasóleo" },
  { k: "petrol", l: "Gasolina" },
  { k: "hybrid", l: "Híbrido" },
  { k: "plug_in_hybrid", l: "Híbrido plug-in" },
  { k: "electric", l: "Elétrico" },
  { k: "lpg", l: "GPL" },
];
const FEATURES = ["wifi", "water", "child_seat", "accessibility", "ac"];
const MAX_PHOTO_BYTES = 4 * 1024 * 1024;

const VEHICLE_DOCS = [
  { key: "dua_front", label: "DUA (frente)" },
  { key: "dua_back", label: "DUA (verso)" },
  { key: "insurance_green_card", label: "Carta Verde de Seguro" },
  { key: "insurance_conditions", label: "Condições Particulares de Seguro" },
  { key: "inspection", label: "Inspecção Automóvel (IPO)" },
];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
}

export default function Vehicles() {
  const { t } = useLang();
  const [vehicles, setVehicles] = useState([]);
  const [status, setStatus] = useState("all");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useViewMode("vehicles", "cards");
  const [missingOnly, setMissingOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    api.get(`/admin/vehicles?${p.toString()}`).then((r) => setVehicles(r.data));
  };
  useEffect(load, [status]); // eslint-disable-line

  // Auto-open editor when navigating with ?edit={vehicleId} (e.g. from Approvals)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.id === editId);
    if (v) {
      setEditing(v);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [vehicles, searchParams, setSearchParams]);

  const grouped = useMemo(() => {
    const g = new Map(CATEGORIES.map((c) => [c.key, []]));
    const list = missingOnly ? vehicles.filter((v) => !(v.main_photo || (v.photos_exterior || [])[0])) : vehicles;
    for (const v of list) {
      if (!g.has(v.type)) g.set(v.type, []);
      g.get(v.type).push(v);
    }
    return g;
  }, [vehicles, missingOnly]);

  const missingCount = useMemo(() => vehicles.filter((v) => !(v.main_photo || (v.photos_exterior || [])[0])).length, [vehicles]);

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow="Frota · Ativos" title={t("nav_vehicles")} description="Cada categoria mostra os veículos disponíveis com marca, modelo, combustível e foto — visível para o cliente na página de reserva.">
        <ViewToggle scope="vehicles" value={mode} onChange={setMode} testId="vehicles-view-toggle" />
        <GoldButton onClick={() => setShow(true)} data-testid="new-vehicle-btn"><Plus className="w-4 h-4" /> {t("add")}</GoldButton>
      </PageHeader>

      <div className="flex gap-1 mb-6 flex-wrap items-center">
        {["all", "available", "in_use", "maintenance", "suspended", "pending"].map((s) => (
          <button key={s} data-testid={`veh-status-${s}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wide border font-bold ${status === s ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-700 hover:text-[#0a0f1e]"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
        <div className="w-4" />
        <button data-testid="veh-missing-photo-filter" onClick={() => setMissingOnly(!missingOnly)}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-wide border font-bold inline-flex items-center gap-1 ${missingOnly ? "border-red-500 text-red-600 bg-red-50" : "border-slate-200 text-slate-700 hover:text-red-600"}`}>
          <Camera className="w-3 h-3" /> Sem foto {missingCount > 0 && <span className={`ml-1 px-1 ${missingOnly ? "bg-red-500 text-white" : "bg-red-100 text-red-700"} font-bold`}>{missingCount}</span>}
        </button>
      </div>

      <div className="space-y-10">
        {CATEGORIES.map((cat) => {
          const list = grouped.get(cat.key) || [];
          return (
            <section key={cat.key} data-testid={`category-${cat.key}`}>
              <div className="flex items-end justify-between mb-4 border-b border-slate-200 pb-2">
                <div>
                  <h2 className="font-serif text-2xl text-[#0a0f1e]">{cat.label}</h2>
                  <p className="text-xs text-slate-700 mt-0.5">{cat.desc}</p>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">{cat.cap_pax} pax · {cat.cap_bags} bag</div>
                  <div className="text-sm font-bold text-[#0a0f1e]">{list.length} {list.length === 1 ? "veículo" : "veículos"}</div>
                </div>
              </div>
              {list.length === 0 ? (
                <div className="border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500 bg-slate-50" data-testid={`category-empty-${cat.key}`}>
                  Sem veículos nesta categoria. Adicione um veículo para começar a operar.
                </div>
              ) : mode === "cards" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {list.map((v) => (
                    <VehicleCard key={v.id} v={v} onEdit={() => setEditing(v)} />
                  ))}
                </div>
              ) : (
                <div className="border border-slate-200 bg-white overflow-x-auto" data-testid={`category-list-${cat.key}`}>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Foto</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Marca / Modelo</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Combustível</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Matrícula</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Cap.</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Categorias</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-700">Estado</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {list.map((v) => (
                        <tr key={v.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`vehicle-row-${v.id}`} onClick={() => setEditing(v)}>
                          <td className="px-3 py-2 w-16">
                            {(v.main_photo || v.photos_exterior?.[0]) ? (
                              <img src={v.main_photo || v.photos_exterior[0]} alt="" className="w-14 h-10 object-cover border border-slate-200" />
                            ) : (
                              <div className="w-14 h-10 bg-slate-100 border border-slate-200 flex items-center justify-center"><Car className="w-4 h-4 text-slate-400" /></div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-[#0a0f1e]">{v.brand} {v.model}</div>
                            <div className="text-[11px] text-slate-600">{v.year} · {v.color}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-700 text-xs uppercase font-semibold">{fuelLabel(v.fuel_type)}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-800 font-bold">{v.plate}</td>
                          <td className="px-3 py-2 text-xs text-slate-700">{v.capacity_passengers} pax · {v.capacity_luggage} bag</td>
                          <td className="px-3 py-2">
                            {(v.assigned_categories || []).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {v.assigned_categories.map((c) => (
                                  <span key={c} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#f59e0b]/10 border border-[#f59e0b]/40 text-[#f59e0b] font-bold">{c}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2"><StatBadge status={v.status} /></td>
                          <td className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">Editar →</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {show && <VehicleEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {editing && <VehicleEditor vehicle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function fuelLabel(k) { return (FUELS.find((f) => f.k === k) || FUELS[0]).l; }

function VehicleCard({ v, onEdit }) {
  const photo = v.main_photo || v.photos_exterior?.[0] || v.photos_interior?.[0];
  return (
    <div data-testid={`vehicle-${v.id}`} onClick={onEdit} className="border border-slate-200 bg-white overflow-hidden hover:border-[#f59e0b] transition-colors cursor-pointer">
      <div className="relative h-40 bg-slate-100 border-b border-slate-200">
        {photo ? (
          <img src={photo} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-amber-700">
            <AlertTriangle className="w-6 h-6" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Sem foto — obrigatória</span>
          </div>
        )}
        <div className="absolute top-2 right-2"><StatBadge status={v.status} /></div>
        <div className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest text-white bg-[#0a0f1e]/80 px-2 py-1 font-bold">Editar</div>
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between">
          <h4 className="font-semibold text-[#0a0f1e] leading-tight">{v.brand} {v.model}</h4>
          <span className="text-[10px] uppercase font-bold text-slate-700 tracking-widest">{v.year}</span>
        </div>
        <div className="text-xs text-slate-700 mt-1">{v.color} · <span className="font-mono font-bold">{v.plate}</span></div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
          <div className="flex items-center gap-1 text-slate-800"><Users className="w-3 h-3 text-slate-500" /> <span className="font-bold">{v.capacity_passengers}</span> pax</div>
          <div className="flex items-center gap-1 text-slate-800"><Briefcase className="w-3 h-3 text-slate-500" /> <span className="font-bold">{v.capacity_luggage}</span> bag</div>
          <div className="flex items-center gap-1 text-slate-800"><Fuel className="w-3 h-3 text-slate-500" /> <span className="font-bold uppercase">{fuelLabel(v.fuel_type)}</span></div>
        </div>
        {(v.features || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {v.features.map((f) => (
              <span key={f} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-200 text-slate-700 font-bold">{f}</span>
            ))}
          </div>
        )}
        {(v.assigned_categories || []).length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Categorias</div>
            <div className="flex flex-wrap gap-1">
              {v.assigned_categories.map((c) => (
                <span key={c} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#f59e0b]/10 border border-[#f59e0b]/40 text-[#f59e0b] font-bold" data-testid={`veh-badge-${v.id}-${c}`}>{c}</span>
              ))}
            </div>
          </div>
        )}
        {v.first_registration_date && (
          <div className="mt-2 text-[10px] text-slate-500">1º registo: <span className="font-mono text-slate-700">{v.first_registration_date}</span></div>
        )}
      </div>
    </div>
  );
}

function VehicleEditor({ vehicle, onClose, onSaved }) {
  const { t } = useLang();
  const isEdit = !!vehicle;
  const [f, setF] = useState(vehicle || { brand: "", model: "", year: 2024, color: "Black", plate: "", type: "executive", fuel_type: "diesel", capacity_passengers: 4, capacity_luggage: 3, features: [], insurance_expiry: "", inspection_expiry: "", first_registration_date: "", assigned_categories: null, main_photo: "", documents: [] });
  const [loading, setLoading] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(null); // { key, docType, label }
  const [allCats, setAllCats] = useState([]);
  const fileRef = useRef(null);
  const toggle = (feat) => setF({ ...f, features: (f.features || []).includes(feat) ? f.features.filter(x => x !== feat) : [...(f.features || []), feat] });

  useEffect(() => {
    api.get("/public/vehicle-categories").then((r) => setAllCats(r.data || [])).catch(() => {});
  }, []);

  // Cascade rule: superior tier covers all lower tiers except van/minibus/tuktuk.
  const cascadeDefault = useMemo(() => {
    const hier = ["economy", "executive", "xl_group"];
    const exclusive = new Set(["van", "minibus", "tuktuk"]);
    const k = f.type;
    if (exclusive.has(k)) return [k];
    const idx = hier.indexOf(k);
    if (idx >= 0) return hier.slice(0, idx + 1);
    return [k];
  }, [f.type]);

  // If admin hasn't touched assigned_categories, follow the cascade
  const effectiveCats = (f.assigned_categories && f.assigned_categories.length > 0) ? f.assigned_categories : cascadeDefault;

  const toggleCat = (key) => {
    const cur = new Set(effectiveCats);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    setF({ ...f, assigned_categories: Array.from(cur) });
  };

  const findDoc = (key) => (f.documents || []).find((x) => x.document_type === key);

  const applyOcrResult = (target, result) => {
    const fields = result.fields || {};
    // Pre-fill top-level vehicle fields when we recognise them
    const patch = {};
    if (target.key === "dua_front") {
      if (fields.plate) patch.plate = String(fields.plate).toUpperCase();
      if (fields.brand) patch.brand = fields.brand;
      if (fields.model) patch.model = fields.model;
      if (fields.colour) patch.color = fields.colour;
      if (fields.seats) patch.capacity_passengers = Number(fields.seats) || f.capacity_passengers;
      if (fields.fuel_type) {
        const fuel = String(fields.fuel_type).toLowerCase();
        const known = FUELS.find((x) => fuel.includes(x.k));
        if (known) patch.fuel_type = known.k;
      }
    }
    if (target.key === "inspection" && fields.next_inspection_date) {
      patch.inspection_expiry = fields.next_inspection_date;
    }
    if ((target.key === "insurance_green_card" || target.key === "insurance_conditions") && fields.valid_to) {
      patch.insurance_expiry = fields.valid_to;
    }
    if (target.key === "dua_back" && fields.next_inspection_date) {
      patch.inspection_expiry = fields.next_inspection_date;
    }
    if (target.key === "dua_back" && fields.insurance_expiry) {
      patch.insurance_expiry = fields.insurance_expiry;
    }

    const docEntry = {
      document_type: target.key,
      number: fields.policy_number || fields.certificate_number || fields.plate || "",
      expiry_date: fields.expiry_date || fields.valid_to || fields.next_inspection_date || null,
      file_url: result.file_url || null,
      ocr_confidence: result.confidence || 0,
      ocr_raw_text: result.raw_text || "",
      extracted_fields: fields,
      uploaded_at: new Date().toISOString(),
    };
    const docs = f.documents || [];
    const idx = docs.findIndex((x) => x.document_type === target.key);
    const nextDocs = idx >= 0
      ? docs.map((x, i) => i === idx ? { ...x, ...docEntry } : x)
      : [...docs, docEntry];
    setF({ ...f, ...patch, documents: nextDocs });
    toast.success(`${target.label} extraído ${result.confidence ? `· ${(result.confidence * 100).toFixed(0)}% confiança` : ""}`);
  };

  const uploadMain = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) { toast.error("Foto excede 4MB"); return; }
    try {
      const url = await uploadImage(file);
      setF({ ...f, main_photo: url });
    } catch (err) {
      toast.error(`Erro ao enviar foto: ${formatApiError(err)}`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.main_photo) { toast.error("A foto do veículo é obrigatória"); return; }
    setLoading(true);
    try {
      if (isEdit) {
        // strip immutable / server-managed fields
        const { id, created_at, ...patch } = f; // eslint-disable-line no-unused-vars
        await api.patch(`/admin/vehicles/${vehicle.id}`, patch);
        toast.success(t("toast_vehicle_updated"));
      } else {
        await api.post("/admin/vehicles", f);
        toast.success(t("toast_vehicle_added"));
      }
      onSaved();
    }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white border border-[#f59e0b]/30 w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto" data-testid="vehicle-editor-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl text-[#0a0f1e]">{isEdit ? `${t("vehicles_edit")} · ${vehicle.plate}` : t("vehicles_new")}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500 hover:text-[#0a0f1e]" /></button>
        </div>

        {/* PHOTO */}
        <div className="mb-6">
          <label className="label-caps block mb-2">Foto do veículo <span className="text-red-600">*</span></label>
          <div className="flex items-center gap-3">
            <div className="w-32 h-24 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {f.main_photo ? <img src={f.main_photo} alt="" className="w-full h-full object-cover" /> : <Car className="w-6 h-6 text-slate-400" />}
            </div>
            <div className="flex-1">
              <button type="button" onClick={() => fileRef.current?.click()} className="h-10 px-4 border border-slate-300 hover:border-[#f59e0b] text-xs uppercase tracking-wide font-bold inline-flex items-center gap-1" data-testid="vehicle-photo-upload">
                <Upload className="w-3.5 h-3.5" /> {f.main_photo ? "Substituir" : "Carregar foto"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadMain} data-testid="vehicle-photo-input" />
              <p className="text-[11px] text-slate-500 mt-2">JPG/PNG/WebP até 4MB. Esta foto aparece ao cliente na página de reserva.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <F l="Marca"><Input required value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} className="input-dt" placeholder="Mercedes" data-testid="veh-brand" /></F>
          <F l="Modelo"><Input required value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className="input-dt" placeholder="E-Class" data-testid="veh-model" /></F>
          <F l="Ano"><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: Number(e.target.value) })} className="input-dt" /></F>
          <F l="Cor"><Input value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="input-dt" /></F>
          <F l="Matrícula"><Input required value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} className="input-dt" placeholder="AA-11-BB" data-testid="veh-plate" /></F>
          <F l="Categoria">
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input-dt w-full" data-testid="veh-type-select">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label} · {c.cap_pax} pax</option>)}
            </select>
          </F>
          <F l="Combustível">
            <select value={f.fuel_type} onChange={(e) => setF({ ...f, fuel_type: e.target.value })} className="input-dt w-full" data-testid="veh-fuel-select">
              {FUELS.map((fu) => <option key={fu.k} value={fu.k}>{fu.l}</option>)}
            </select>
          </F>
          <F l="Passageiros"><Input type="number" value={f.capacity_passengers} onChange={(e) => setF({ ...f, capacity_passengers: Number(e.target.value) })} className="input-dt" /></F>
          <F l="Bagagem"><Input type="number" value={f.capacity_luggage} onChange={(e) => setF({ ...f, capacity_luggage: Number(e.target.value) })} className="input-dt" /></F>
          <F l="Validade seguro"><Input type="date" value={f.insurance_expiry} onChange={(e) => setF({ ...f, insurance_expiry: e.target.value })} className="input-dt" /></F>
          <F l="Validade IPO"><Input type="date" value={f.inspection_expiry} onChange={(e) => setF({ ...f, inspection_expiry: e.target.value })} className="input-dt" /></F>
          <F l="Data 1º registo"><Input type="date" value={f.first_registration_date || ""} onChange={(e) => setF({ ...f, first_registration_date: e.target.value })} className="input-dt" data-testid="veh-first-reg" /></F>
          <F l="Estado"><span className="text-sm text-slate-700 font-mono">{f.status || "pending"} {f.status === "pending" && <em className="text-amber-700 text-[10px]">(aprovar em Aprovações)</em>}</span></F>

          {/* Categorias atribuídas — admin escolhe as gamas que este veículo pode servir */}
          <div className="col-span-2">
            <label className="label-caps block mb-2">
              Categorias atribuídas
              <span className="ml-2 text-[10px] normal-case tracking-normal text-slate-500 font-normal">
                (sugestão automática: gamas superiores cobrem inferiores, exceto van/minibus/tuktuk)
              </span>
            </label>
            {allCats.length === 0 ? (
              <div className="text-xs text-slate-500">A carregar categorias…</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="veh-categories">
                {allCats.map((c) => {
                  const active = effectiveCats.includes(c.key);
                  return (
                    <button key={c.key} type="button" onClick={() => toggleCat(c.key)}
                      data-testid={`veh-cat-${c.key}`}
                      className={`px-3 py-1.5 text-[10px] uppercase border font-bold ${active ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10" : "border-slate-200 text-slate-500"}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}
            {(!f.assigned_categories || f.assigned_categories.length === 0) && (
              <div className="mt-2 text-[10px] text-slate-500">A usar sugestão automática: <span className="font-mono">{cascadeDefault.join(", ") || "—"}</span>. Clique para personalizar.</div>
            )}
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-2">Extras</label>
            <div className="flex flex-wrap gap-2">
              {FEATURES.map((feat) => (
                <button key={feat} type="button" onClick={() => toggle(feat)}
                  className={`px-3 py-1.5 text-[10px] uppercase border font-bold ${f.features.includes(feat) ? "border-[#f59e0b] text-[#f59e0b] bg-[#f59e0b]/10" : "border-slate-200 text-slate-700"}`}>
                  {feat}
                </button>
              ))}
            </div>
          </div>

          {/* OCR — Documentos do veículo */}
          <div className="col-span-2 mt-4 border border-[#f59e0b]/30 bg-amber-50/40 p-4">
            <div className="flex items-center gap-2 mb-3">
              <ScanLine className="w-4 h-4 text-[#f59e0b]" />
              <div className="label-caps">Documentos do veículo · OCR Gemini Vision</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {VEHICLE_DOCS.map((doc) => {
                const existing = findDoc(doc.key);
                return (
                  <div key={doc.key} className={`border p-3 bg-white ${existing ? "border-emerald-300" : "border-slate-200"}`} data-testid={`veh-doc-card-${doc.key}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-semibold text-sm text-[#0a0f1e]">{doc.label}</div>
                        {existing ? (
                          <div className="text-[10px] font-mono text-slate-600 mt-1">
                            {existing.number && <>Nº <span className="text-[#0a0f1e] font-bold">{existing.number}</span></>}
                            {existing.expiry_date && <span className="ml-2">Val: <span className="text-[#0a0f1e] font-bold">{existing.expiry_date}</span></span>}
                            {existing.ocr_confidence != null && <span className="ml-2 text-emerald-700">✓ OCR {(existing.ocr_confidence * 100).toFixed(0)}%</span>}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-500 mt-1">Ainda não carregado</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setOcrOpen({ key: doc.key, docType: doc.key, label: doc.label })}
                        data-testid={`veh-ocr-btn-${doc.key}`}
                        className="text-[10px] uppercase tracking-widest font-bold border border-[#f59e0b] text-[#f59e0b] hover:bg-amber-50 px-3 py-2"
                      >
                        <Upload className="w-3 h-3 inline mr-1" /> {existing ? "Substituir" : "Carregar"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* OCR modal */}
        {ocrOpen && (
          <OCRUploadModal
            documentType={ocrOpen.docType}
            label={ocrOpen.label}
            onCancel={() => setOcrOpen(null)}
            onConfirm={(res) => { applyOcrResult(ocrOpen, res); setOcrOpen(null); }}
          />
        )}

        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
          <GhostButton type="button" onClick={onClose} className="flex-1">{t("action_cancel")}</GhostButton>
          <GoldButton type="submit" disabled={loading} className="flex-1 justify-center" data-testid="veh-submit">{loading ? t("toast_saved") : (isEdit ? t("toast_save_changes") : t("vehicles_create"))}</GoldButton>
        </div>
      </form>
    </div>
  );
}

function F({ l, children }) { return (<div><label className="label-caps block mb-2">{l}</label>{children}</div>); }
